import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import {
  entitlementSwitchable,
  getEntitlement,
  getPlaybackUsage,
  hydratePlaybackUsage,
  PLAYBACK_LIMIT_SECONDS,
  resetPlaybackUsage,
  setEntitlement,
  type EntitlementMode,
} from "../library/playbackUsageStore.js";

interface CapacitorLike { isNativePlatform?: () => boolean; }

function nativeApk(): boolean {
  return (globalThis as { Capacitor?: CapacitorLike }).Capacitor?.isNativePlatform?.() === true;
}

function minutes(seconds: number): string {
  return Number.isFinite(seconds) ? `${Math.floor(seconds / 60)} min` : "unlimited";
}

/**
 * Temporary APK-only control until server activation and Play Billing exist. Test builds
 * only (owner, 2026-10-20): a release build is what it was built as (native
 * default_entitlement) and shows no switch.
 */
export function EntitlementCard() {
  const [mode, setMode] = useState<EntitlementMode>(() => getEntitlement());
  const [usage, setUsage] = useState(() => getPlaybackUsage());
  const [offered, setOffered] = useState(() => entitlementSwitchable());
  const isNative = nativeApk();
  const paidRef = useRef<HTMLButtonElement>(null);
  // Arrived from the "free time is used up" prompt's Upgrade: straight to Paid.
  const fromUpgrade = (useLocation().state as { focus?: string } | null)?.focus === "licensing";
  useEffect(() => {
    if (!isNative || !fromUpgrade) return;
    paidRef.current?.scrollIntoView({ block: "center" });
    paidRef.current?.focus();
  }, [isNative, fromUpgrade]);
  useEffect(() => {
    if (!isNative) return;
    void hydratePlaybackUsage().then(() => {
      setMode(getEntitlement());
      setUsage(getPlaybackUsage());
      setOffered(entitlementSwitchable());
    });
  }, [isNative]);
  if (!isNative || !offered) return null;

  const changeMode = (next: EntitlementMode) => {
    setEntitlement(next);
    setMode(next);
  };
  const reset = () => {
    resetPlaybackUsage();
    setUsage(getPlaybackUsage());
  };

  return (
    <div className="settings-card">
      <h2 className="settings-card-title">Temporary licensing test</h2>
      <p className="settings-card-desc">
        APK testing only. Free mode enforces the per-device playback limits;
        Paid mode simulates an unlocked entitlement. This will be replaced by
        activation codes and Google Play Billing.
      </p>
      <div className="settings-row">
        <span className="settings-row-label">Entitlement</span>
        <div>
          <button type="button" className={`ui-btn ${mode === "free" ? "ui-btn--primary" : ""}`} onClick={() => changeMode("free")} aria-pressed={mode === "free"}>Free</button>{" "}
          <button ref={paidRef} type="button" className={`ui-btn ${mode === "paid" ? "ui-btn--primary" : ""}`} onClick={() => changeMode("paid")} aria-pressed={mode === "paid"}>Paid</button>
        </div>
      </div>
      <div className="settings-row">
        <span className="settings-row-label">Usage</span>
        <span className="settings-row-value">
          Live {minutes(usage.live)} / {minutes(PLAYBACK_LIMIT_SECONDS.live)} · Series {minutes(usage.series)} / {minutes(PLAYBACK_LIMIT_SECONDS.series)} · Movies {minutes(usage.movie)} / {minutes(PLAYBACK_LIMIT_SECONDS.movie)}
        </span>
      </div>
      <div className="settings-row">
        <button type="button" className="ui-btn ui-btn--ghost" onClick={reset}>Reset test usage</button>
        <span className="settings-row-hint">Installation-local test state; not a production unlock.</span>
      </div>
    </div>
  );
}
